import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../../app/providers';
import type { LoginCredentials, SignupCredentials } from '../types';

export const useLogin = () => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const mutate = async (credentials: LoginCredentials) => {
    setIsPending(true);
    setError(null);
    try {
      const userData = await login(credentials);

      if (!userData) {
        navigate({ to: '/sign-in' });
        return;
      }
      const { role, isOnboardingComplete, subscription } = userData;

      if (role === 'PENDING') {
        navigate({ to: '/onboarding/role' });
      } else if (!isOnboardingComplete) {
        let path = '/onboarding/organization';

        // If owner has valid role but no subscription or invalid status, force package selection or update
        // Allowed statuses: ACTIVE, TRIALING, PAST_DUE (grace period)
        const validStatuses = ['ACTIVE', 'TRIALING', 'PAST_DUE'];
        const hasValidSubscription =
          subscription && validStatuses.includes(subscription.status);

        if ((role === 'OWNER' || role === 'ADMIN') && !hasValidSubscription) {
          path = '/onboarding/packages';
        } else if (role === 'RESIDENT') {
          path = '/resident/onboarding/houses';
        }
        navigate({ to: path });
      } else if (role === 'RESIDENT') {
        navigate({ to: '/resident/dashboard' });
      }

      if (role !== 'RESIDENT' && isOnboardingComplete) {
        navigate({ to: '/' });
      }
      //  else {
      // }
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate,
    isPending,
    error,
  };
};

export const useSignup = () => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const mutate = async (credentials: SignupCredentials) => {
    setIsPending(true);
    setError(null);
    try {
      await signup(credentials);
      // New users always need onboarding (PENDING role)
      navigate({ to: '/onboarding/role' });
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate,
    isPending,
    error,
  };
};

export const useLogout = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const mutate = () => {
    logout();
    navigate({ to: '/sign-in' });
  };

  return {
    mutate,
  };
};
